import { InputNumber, Table, type TableProps } from 'antd';
import { get } from 'lodash/fp';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { AmountUnit } from '../../../../../api/types';
import FormFields from '../../../../../themes/admin/Fields/FormFields';

const StyledTable = styled(Table)<{ unitIsPercentage: boolean }>`
    .editable-cell {
        position: relative;
    }

    .editable-cell-value-wrapper {
        width: 120px;
        height: 32px;
        cursor: pointer;
        padding: 4px 11px;
        border: 1px solid #d9d9d9;
        border-radius: 2px;
    }

    .ant-input-number {
        width: 120px;
    }

    ${props =>
        props.unitIsPercentage &&
        css`
            .editable-cell-value-wrapper,
            .ant-input-number {
                width: 80px;
            }
        `};

    .variant-column {
        min-width: 30vw;
        max-width: 350px;
    }

    .terms-column {
        width: 120px;
    }

    ${props =>
        props.unitIsPercentage &&
        css`
            .editable-cell-value-wrapper,
            .ant-input-number {
                width: 80px;
            }

            .terms-column {
                width: 80px;
            }
        `};
`;

type EditableCellProps<TRecord extends object = any> = {
    children: ReactNode;
    isEditable?: boolean;
    dataIndex?: keyof TRecord;
    record?: TRecord;
    onUpdate?: (record: TRecord) => unknown;
    valueUnit?: AmountUnit;
};

const EditableCell = <TCellProps extends EditableCellProps>({
    children,
    isEditable,
    dataIndex,
    record,
    onUpdate,
    valueUnit,
    ...otherProps
}: TCellProps) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);

    const toggleEdit = useCallback(() => {
        setEditing(!editing);
    }, [editing, setEditing]);

    const currentValue = get(dataIndex, record);

    // internal value
    const [value, setValue] = useState(currentValue);
    useEffect(() => {
        setValue(currentValue);
    }, [currentValue]);

    const onChange = useCallback(
        input => {
            setValue(valueUnit?.match(AmountUnit.Percentage) && input > 100 ? 100 : input);
        },
        [valueUnit]
    );

    const onSave = useCallback(() => {
        setEditing(false);
        if (onUpdate && record) {
            onUpdate({ ...record, [dataIndex]: valueUnit?.match(AmountUnit.Percentage) && value > 100 ? 100 : value });
        }
    }, [onUpdate, record, dataIndex, valueUnit, value]);

    useEffect(() => {
        if (editing) {
            inputRef.current.focus();
        }
    }, [editing]);

    let childNode = children;
    if (isEditable) {
        childNode = editing ? (
            <InputNumber
                ref={ref => {
                    inputRef.current = ref;
                }}
                max={valueUnit?.match(AmountUnit.Percentage) ? 100 : false}
                min={0}
                onBlur={onSave}
                onChange={onChange}
                onPressEnter={onSave}
                type="number"
                value={value}
            />
        ) : (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events
            <div className="editable-cell-value-wrapper" onClick={toggleEdit}>
                {children}
            </div>
        );
    }

    return <td {...otherProps}>{childNode}</td>;
};

type DefaultColumn = {
    title: ReactNode;
    dataIndex: string;
    fixed?: 'left' | 'right' | boolean;
    render?: TableProps<any>['columns'][number]['render'];
    filters?: { text: string; value: string }[];
    onFilter?: (value, record) => boolean;
    className?: string;
    width?: number;
};

export type Columns<TRecord, TColumn extends DefaultColumn = DefaultColumn> = Array<
    TColumn & {
        onCell?: (record: TRecord) => {
            record: TRecord;
            dataIndex: TColumn['dataIndex'];
            isEditable: boolean;
            onUpdate: (record: TRecord) => void;
            valueUnit?: AmountUnit;
        };
    }
>;

export type EditableTableProps<TRecord extends object, TColumns extends DefaultColumn = DefaultColumn> = {
    columns: Columns<TRecord, TColumns>;
    records: TRecord[];
    unitIsPercentage?: boolean;
};

const EditableTable = <TRecord extends object, TColumns extends DefaultColumn = DefaultColumn>({
    columns,
    records,
    unitIsPercentage,
}: EditableTableProps<TRecord, TColumns>) => (
    <StyledTable
        columns={columns}
        components={{
            body: {
                cell: EditableCell,
            },
        }}
        dataSource={records}
        rowClassName="editable-row"
        scroll={{ x: true }}
        unitIsPercentage={unitIsPercentage}
    />
);
export default EditableTable;
